import type { ProfileOverrideBody, RunRequestBody } from '../models/impact.dto'
import type {
  AnalysisDraft,
  AnalysisSchedule,
  CausalFilterDto,
  CausalProfile,
  RunInputParameters,
} from '../models/impact.types'
import type { ImpactRecommendationPrefill } from '../models/recommendation.types'

import type { FilterMatchMode, ReportColumn, ReportFilter } from '@/features/reports'
import { i18n } from '@/shared/i18n'
import { toApiDate } from '@/shared/lib/date/toApiDate'
import { useFormat } from '@/shared/lib/format'

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MATCH_MODES = new Set<FilterMatchMode>([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'greater_than_or_equal_to',
  'less_than',
  'less_than_or_equal_to',
  'between',
  'regex',
  'not_regex',
  'is_empty',
  'is_not_empty',
])

export type ScheduleSeedMode = 'edit' | 'duplicate'

function dateOrNow(value: string | null | undefined): Date {
  if (!value) return new Date()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

function filterFromDto(filter: CausalFilterDto): ReportFilter {
  const candidate = filter.operator.toLowerCase() as FilterMatchMode
  return {
    field: filter.field,
    match_mode: MATCH_MODES.has(candidate) ? candidate : 'equals',
    value: filter.value,
    ...(filter.is_change === undefined ? {} : { is_change: filter.is_change }),
  }
}

/** The single schedule-to-draft funnel used by both edit and duplicate entry points. */
export function fromSchedule(
  schedule: AnalysisSchedule,
  profiles: readonly CausalProfile[],
  mode: ScheduleSeedMode = 'edit',
): AnalysisDraft {
  const profile = profiles.find((entry) => entry.slug === schedule.profile_slugs[0]) ?? null
  const fanout = profile?.config.fanout
  const parameters = schedule.input_parameters
  const interventionMode = Boolean(parameters.intervention_date)
  const filters = [
    ...(profile?.config.change_source?.filters ?? []),
    ...(profile?.config.data_source?.filters ?? []),
  ].map(filterFromDto)

  return {
    editScheduleId: mode === 'edit' ? schedule.id : null,
    analysisName: mode === 'duplicate' ? '' : schedule.name,
    customName: mode === 'duplicate' ? '' : schedule.name,
    profileSlug: profile?.slug ?? schedule.profile_slugs[0] ?? '',
    selectedProfile: profile,
    selectedLevel: null,
    selectedDimension: null,
    targetMetric: null,
    controlVariables: [],
    filters,
    dateMode: interventionMode ? 'intervention' : 'range',
    interventionDate: dateOrNow(parameters.intervention_date),
    dateRange: {
      start: dateOrNow(parameters.date_start),
      end: dateOrNow(parameters.date_end),
    },
    datePreset: parameters.date_interval_days ? String(parameters.date_interval_days) : null,
    baselinePeriod: fanout?.pre_days ?? null,
    observationPeriod: fanout?.post_days ?? null,
    waitBeforeAnalysis: fanout?.min_days_after_intervention ?? null,
    minTotalClicks: fanout?.min_clicks_total ?? null,
    minActiveDaysBaseline: fanout?.min_unique_days ?? null,
    minActiveDaysObservation: fanout?.min_post_unique_days ?? null,
    scheduleFrequency: mode === 'duplicate' ? 'one_time' : schedule.frequency,
    scheduleHour: mode === 'duplicate' ? null : (schedule.local_time ?? null),
    scheduleDay:
      mode === 'duplicate' || schedule.day_of_week == null
        ? null
        : (WEEKDAYS[schedule.day_of_week] ?? null),
    scheduleDayOfMonth: mode === 'duplicate' ? null : (schedule.day_of_month ?? null),
    maxCreditsPerAnalysis: schedule.max_credits ?? 50,
    // Hydrated separately from the schedule's notification subscriptions (edit mode only) —
    // see `useAnalysisWizard`'s `hydrateNotifications`.
    notifyChannels: [],
    notifyEmails: [],
    notifyWhatsapp: [],
  }
}

/**
 * Legacy `applyRecommendationPrefill()` (`ImpactAnalysisModal.vue`): a blank draft with the
 * recommendation's name, range and profile written over it.
 *
 * Only those four things are seeded. The `measurement` summary is deliberately NOT written into
 * the form — v3 showed it read-only beside the wizard and let the numbers reach the backend inside
 * `run_payload` instead, so the user cannot half-edit a measurement design the engine chose.
 * `frequency` is `one_time` (the service already pinned it) and `datePreset` is `Custom`, because
 * the recommended window is a measured fact about a change that happened, not a rolling preset.
 *
 * `profile` is the SELECTABLE profile or `null`: an inactive or non-workable recommended profile
 * leaves the picker empty so the wizard can say "choose another one" (`findSelectableProfile`).
 */
export function fromRecommendation(
  prefill: ImpactRecommendationPrefill,
  profile: CausalProfile | null,
): AnalysisDraft {
  const payload = prefill.runPayload
  return {
    ...blank(),
    analysisName: payload.name,
    profileSlug: profile?.slug ?? '',
    selectedProfile: profile,
    dateMode: 'range',
    dateRange: {
      start: dateOrNow(payload.input_parameters.date_start),
      end: dateOrNow(payload.input_parameters.date_end),
    },
    datePreset: 'Custom',
    scheduleFrequency: 'one_time',
    // An engine payload with no positive cap keeps `blank()`'s 50 instead of reaching the backend
    // uncapped — the user can still raise or clear it, but never inherits an uncapped run unasked.
    ...(payload.max_credits && payload.max_credits > 0
      ? { maxCreditsPerAnalysis: payload.max_credits }
      : {}),
    /** Carried so `toRunBody` can hand back every payload key the wizard does not own. */
    recommendationPayload: payload,
  }
}

/** Legacy `setDefaultProfile()` (`ImpactAnalysisModal.vue`) — the first workable profile, sorted
 *  workable-first then by id, is pre-selected for a brand-new analysis rather than leaving the
 *  picker empty. */
export function defaultProfile(profiles: readonly CausalProfile[]): CausalProfile | null {
  if (!profiles.length) return null
  const sorted = [...profiles].sort((left, right) => {
    const leftDisabled = left.config.frontend_workable === false
    const rightDisabled = right.config.frontend_workable === false
    if (leftDisabled !== rightDisabled) return leftDisabled ? 1 : -1
    return (left.id ?? 0) - (right.id ?? 0)
  })
  return sorted[0] ?? null
}

/** A blank draft for "New Analysis" — mirrors legacy `analysisForm`'s initial reactive state. */
export function blank(): AnalysisDraft {
  return {
    editScheduleId: null,
    analysisName: '',
    customName: '',
    profileSlug: '',
    selectedProfile: null,
    selectedLevel: null,
    selectedDimension: null,
    targetMetric: null,
    controlVariables: [],
    filters: [],
    dateMode: 'range',
    interventionDate: new Date(),
    dateRange: { start: new Date(new Date().setDate(new Date().getDate() - 14)), end: new Date() },
    datePreset: 'Last 14 Days',
    baselinePeriod: null,
    observationPeriod: null,
    waitBeforeAnalysis: null,
    minTotalClicks: null,
    minActiveDaysBaseline: null,
    minActiveDaysObservation: null,
    scheduleFrequency: 'one_time',
    scheduleHour: null,
    scheduleDay: null,
    scheduleDayOfMonth: null,
    maxCreditsPerAnalysis: 50,
    notifyChannels: [],
    notifyEmails: [],
    notifyWhatsapp: [],
  }
}

/** `ReportFilter` → the wire shape old's `upsertOverride`/`runAnalysis` send. */
function filterToDto(filter: ReportFilter): CausalFilterDto {
  return { field: filter.field, operator: filter.match_mode.toUpperCase(), value: filter.value }
}

/**
 * Re-seeds the form from a profile's own config (`SetupStep.vue`'s `syncFormWithProfile`) —
 * called whenever the selected profile changes. `metrics` is the level's numeric column set
 * (`ReportLevel.tableMetrics`); a target/control lookup miss leaves the draft's prior value
 * alone, matching legacy's `if (found) …` guard exactly.
 */
export function syncFromProfile(
  profile: CausalProfile,
  metrics: readonly ReportColumn[],
): Partial<AnalysisDraft> {
  const fanout = profile.config.fanout
  const targetKey = fanout?.target_controls?.target_metric
  const targetMetric = targetKey
    ? (metrics.find((metric) => metric.field.toLowerCase() === targetKey.toLowerCase()) ?? null)
    : null

  const controlKeys = fanout?.target_controls?.control_metrics ?? []
  const controlVariables = controlKeys
    .map((key) => metrics.find((metric) => metric.field.toLowerCase() === key.toLowerCase()))
    .filter((metric): metric is ReportColumn => metric !== undefined)

  const intervalDays = profile.config.change_source?.date_interval_days ?? 14
  const end = new Date()
  end.setHours(0, 0, 0, 0)
  const start = new Date(end)
  start.setDate(end.getDate() - intervalDays + 1)

  return {
    baselinePeriod: fanout?.pre_days ?? null,
    observationPeriod: fanout?.post_days ?? null,
    minTotalClicks: fanout?.min_clicks_total ?? null,
    waitBeforeAnalysis: fanout?.min_days_after_intervention ?? null,
    minActiveDaysBaseline: fanout?.min_unique_days ?? null,
    minActiveDaysObservation: fanout?.min_post_unique_days ?? null,
    dateRange: { start, end },
    filters: (profile.config.data_source?.filters ?? []).map(filterFromDto),
    ...(targetMetric ? { targetMetric } : {}),
    ...(controlVariables.length ? { controlVariables } : {}),
  }
}

/** Legacy `hasChanges()` — true when the draft has drifted from the selected profile's own
 *  config, meaning a profile override must be saved before the run/schedule request fires. */
export function hasProfileChanges(draft: AnalysisDraft): boolean {
  const profile = draft.selectedProfile
  if (!profile || profile.slug === 'custom') return false

  const fanout = profile.config.fanout ?? {}
  const currentTarget = draft.targetMetric?.field.toLowerCase() ?? ''
  const originalTarget = fanout.target_controls?.target_metric?.toLowerCase() ?? ''
  if (currentTarget !== originalTarget) return true

  const currentControls = draft.controlVariables.map((metric) => metric.field.toLowerCase()).sort()
  const originalControls = (fanout.target_controls?.control_metrics ?? [])
    .map((metric) => metric.toLowerCase())
    .sort()
  if (JSON.stringify(currentControls) !== JSON.stringify(originalControls)) return true

  if (draft.baselinePeriod !== (fanout.pre_days ?? null)) return true
  if (draft.observationPeriod !== (fanout.post_days ?? null)) return true
  if (draft.waitBeforeAnalysis !== (fanout.min_days_after_intervention ?? null)) return true
  if (draft.minTotalClicks !== (fanout.min_clicks_total ?? null)) return true
  if (draft.minActiveDaysBaseline !== (fanout.min_unique_days ?? null)) return true
  if (draft.minActiveDaysObservation !== (fanout.min_post_unique_days ?? null)) return true

  const currentFilters = JSON.stringify(draft.filters.map(filterToDto))
  const originalFilters = JSON.stringify(profile.config.data_source?.filters ?? [])
  return currentFilters !== originalFilters
}

/** Legacy `upsertOverride()`'s payload — `null` when there's no profile to override. */
export function toOverrideBody(draft: AnalysisDraft): ProfileOverrideBody | null {
  const profile = draft.selectedProfile
  if (!profile) return null
  return {
    global_profile: profile.global_profile_id ?? profile.id ?? 0,
    is_active: true,
    override_config: {
      fanout: {
        target_controls: {
          target_metric: draft.targetMetric?.field.toLowerCase() ?? '',
          control_metrics: draft.controlVariables.map((metric) => metric.field.toLowerCase()),
        },
        pre_days: draft.baselinePeriod,
        post_days: draft.observationPeriod,
        min_clicks_total: draft.minTotalClicks,
        min_days_after_intervention: draft.waitBeforeAnalysis,
        min_unique_days: draft.minActiveDaysBaseline,
        min_post_unique_days: draft.minActiveDaysObservation,
      },
      data_source: { filters: draft.filters.map(filterToDto) },
    },
  }
}

/**
 * The name the user gets when they save without typing one. Unlike presentation copy this is a
 * WIRE value — it lands in `RunRequestBody.name`, the backend stores it, and every later screen
 * renders it verbatim — so it cannot be deferred to a component the way a `labelKey` is. Both of
 * its locale-bearing halves therefore resolve here, through the sanctioned funnels rather than
 * raw ECMA-402: the date via `useFormat()` (BRIEF §3.6 — its default shape is the same
 * `year`/`month:'short'`/`day` this used to spell out as `toLocaleDateString('en-US', …)`, so
 * en-US output is unchanged), and the no-profile fallback via `i18n.global.t` — the
 * out-of-component key resolution the i18n barrel exists to serve (contract 04 §8). Neither
 * import breaches `service/**` purity: no Vue, no Pinia, no transport (contract 13 §9/§16).
 */
function autoName(draft: AnalysisDraft): string {
  const trimmed = draft.analysisName.trim()
  if (trimmed) return trimmed
  const label = draft.selectedProfile?.name ?? i18n.global.t('impactAnalysis.autoName.fallback')
  return `${label} - ${useFormat().formatDate(new Date())}`
}

/** Legacy `runAnalysis()`'s `params` object — the POST/PUT `schedules/` body. */
export function toRunBody(draft: AnalysisDraft, workspaceTimezone: string | null): RunRequestBody {
  const inputParams = draft.selectedProfile?.config.input_params ?? []
  let input_parameters: RunInputParameters
  if (inputParams.includes('intervention_date')) {
    input_parameters = { intervention_date: toApiDate(draft.interventionDate) }
  } else if (draft.datePreset && draft.datePreset !== 'Custom') {
    const days =
      Math.round((draft.dateRange.end.getTime() - draft.dateRange.start.getTime()) / 86_400_000) + 1
    input_parameters = { date_interval_days: days }
  } else {
    input_parameters = {
      date_start: toApiDate(draft.dateRange.start),
      date_end: toApiDate(draft.dateRange.end),
    }
  }

  const isRecurring = draft.scheduleFrequency !== 'one_time'
  const maxCredits = Number(draft.maxCreditsPerAnalysis)

  // v3 spread `...recommendationPrefill.runPayload` FIRST and then overrode the fields the wizard
  // owns, so any key the engine authored and the form has no control for survives the round trip.
  // The cast is the price of that: `RunRequestBody` is a closed interface and the extras are
  // `unknown` by construction, but every REQUIRED field is written below the spread.
  // `max_credits` is form-owned too, so the payload's copy is dropped before the spread: a cleared
  // field must send no cap, not silently resurrect the engine's.
  const { max_credits: _payloadCap, ...enginePayload } = draft.recommendationPayload ?? {}
  const body = {
    ...enginePayload,
    name: autoName(draft),
    profile_slugs: [draft.selectedProfile?.slug ?? null],
    input_parameters,
    frequency: draft.scheduleFrequency,
    ...(maxCredits > 0 ? { max_credits: maxCredits } : {}),
    ...(isRecurring && draft.scheduleHour ? { local_time: draft.scheduleHour } : {}),
    ...(isRecurring && workspaceTimezone ? { timezone: workspaceTimezone } : {}),
    ...(draft.scheduleFrequency === 'weekly' && draft.scheduleDay
      ? { day_of_week: WEEKDAYS.indexOf(draft.scheduleDay) }
      : {}),
    ...(draft.scheduleFrequency === 'monthly' && draft.scheduleDayOfMonth
      ? { day_of_month: draft.scheduleDayOfMonth }
      : {}),
  }
  return body as RunRequestBody
}

/** Legacy `runAnalysis()`'s `isNameOnly` check — an edit that only renamed the schedule updates
 *  it in place instead of delete-and-recreate. */
export function isNameOnlyEdit(body: RunRequestBody, original: AnalysisSchedule): boolean {
  const originalParams = original.input_parameters
  return (
    body.profile_slugs[0] === original.profile_slugs[0] &&
    body.frequency === original.frequency &&
    (body.input_parameters.date_start ?? null) === (originalParams.date_start ?? null) &&
    (body.input_parameters.date_end ?? null) === (originalParams.date_end ?? null) &&
    (body.input_parameters.intervention_date ?? null) ===
      (originalParams.intervention_date ?? null) &&
    (body.local_time ?? null) === (original.local_time ?? null) &&
    (body.day_of_week ?? null) === (original.day_of_week ?? null) &&
    (body.day_of_month ?? null) === (original.day_of_month ?? null)
  )
}

export const analysisDraftMapper = {
  fromSchedule,
  fromRecommendation,
  blank,
  defaultProfile,
  syncFromProfile,
  hasProfileChanges,
  toOverrideBody,
  toRunBody,
  isNameOnlyEdit,
}
